import { useEffect, useId, useState, type ReactNode } from 'react';
import { maintenant } from '../horloge';

const MOIS = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
] as const;

/**
 * Les libellés propres à ce champ — les douze mois et les trois intitulés de liste.
 *
 * En français par défaut : la grande majorité des appels ne viennent pas encore d'un
 * écran traduit, et un champ qui basculerait tout seul en anglais parce que le navigateur
 * du parent l'est romprait ce que le reste de l'écran continue de dire en français.
 * Un appelant traduit passe la sienne — voir `CreerDossier`.
 */
export interface TraductionChampDate {
  mois: readonly [
    string, string, string, string, string, string,
    string, string, string, string, string, string,
  ];
  jour: string;
  moisLabel: string;
  annee: string;
}

const TRADUCTION_FRANCAISE: TraductionChampDate = {
  mois: MOIS,
  jour: 'Jour',
  moisLabel: 'Mois',
  annee: 'Année',
};

/** Nombre de jours du mois, année bissextile comprise. */
function joursDuMois(annee: number, mois: number): number {
  if (!annee || !mois) return 31;
  return new Date(Date.UTC(annee, mois, 0)).getUTCDate();
}

interface PartiesDate {
  jour: number;
  mois: number;
  annee: number;
}

function decomposer(valeur: string): PartiesDate {
  const [annee = '', mois = '', jour = ''] = valeur.split('-');
  return { jour: Number(jour) || 0, mois: Number(mois) || 0, annee: Number(annee) || 0 };
}

/** `AAAA-MM-JJ`, ou vide tant que les trois parties ne sont pas là. */
function composerTexte({ jour, mois, annee }: PartiesDate): string {
  if (!jour || !mois || !annee) return '';
  return `${annee}-${String(mois).padStart(2, '0')}-${String(jour).padStart(2, '0')}`;
}

/** Un enfant du dossier a moins de 25 ans ; au-delà, la liste devient un obstacle. */
const ANNEES_EN_ARRIERE = 25;

/**
 * Fenêtre d'années proposée, autour de l'année courante.
 *
 * Une date de naissance regarde loin en arrière et jamais en avant ; une date de dépense
 * ou d'échange de jours se joue à quelques mois près, des deux côtés. Une seule liste
 * fixe servirait mal les deux — d'où ce réglage, plutôt qu'un second composant qui
 * finirait par diverger sur les mois bissextiles ou la resynchronisation.
 */
export interface FenetreAnnees {
  avant: number;
  apres: number;
}

/**
 * Saisie d'une date : trois listes, jour / mois / année.
 *
 * Elle remplace `<input type="date">`, pour deux raisons dont la première est un vrai
 * défaut constaté :
 *
 * 1. **Le champ natif suit la locale du navigateur, pas celle de la page.** `lang="fr"` ne
 *    change rien : un navigateur configuré en anglais affiche `mm/dd/yyyy` dans une
 *    application française. Le parent tape alors le jour dans la case du mois, la valeur
 *    est refusée au-delà de 12, et le jour paraît impossible à régler. C'est exactement le
 *    symptôme qui a été remonté.
 * 2. **Un calendrier est le mauvais outil pour une date éloignée.** Atteindre 2017 depuis
 *    aujourd'hui demande de reculer neuf ans de mois en mois ; une liste d'années s'ouvre
 *    et se choisit.
 *
 * `fenetre` règle les années proposées : loin en arrière pour une naissance, quelques
 * mois de part et d'autre pour une dépense ou un échange de jours.
 *
 * La liste des jours est bornée par le mois choisi : le 31 février n'est jamais proposé,
 * plutôt que refusé après coup.
 *
 * La valeur reste au format `AAAA-MM-JJ`, celui de l'API. Tant que les trois listes ne sont
 * pas renseignées, elle est vide — un formulaire ne doit pas croire tenir une date sur deux
 * tiers de saisie.
 */
export function ChampDate({
  label,
  value,
  onChange,
  aide,
  required,
  fenetre = { avant: ANNEES_EN_ARRIERE, apres: 0 },
  traduction = TRADUCTION_FRANCAISE,
}: {
  /** Voir `Champ` : la part invisible d'un libellé lève l'ambiguïté à l'oreille. */
  label: ReactNode;
  /** `AAAA-MM-JJ`, ou chaîne vide. */
  value: string;
  onChange: (valeur: string) => void;
  aide?: string;
  required?: boolean;
  fenetre?: FenetreAnnees;
  traduction?: TraductionChampDate;
}) {
  const id = useId();
  const aideId = `${id}-aide`;

  // Les trois listes vivent en état local, et non déduites de `value`.
  //
  // C'est nécessaire, et ça a d'abord été raté : `value` reste vide tant que les trois
  // listes ne sont pas remplies — sinon un formulaire croirait tenir une date sur deux
  // tiers de saisie. Relire les trois parties depuis `value` faisait donc **oublier** le
  // mois dès qu'on choisissait l'année, et la saisie n'aboutissait jamais.
  const [parties, setParties] = useState(() => decomposer(value));

  // On se resynchronise quand la valeur vient d'ailleurs — une fiche rechargée, une
  // correction annulée — mais jamais sur l'écho de ce qu'on vient d'émettre.
  useEffect(() => {
    const recue = decomposer(value);
    if (value && composerTexte(recue) !== composerTexte(parties)) setParties(recue);
    // `parties` est délibérément hors des dépendances : la synchronisation ne suit que la
    // valeur reçue, sinon chaque frappe se réécrirait elle-même.
  }, [value]);

  const { jour, mois, annee } = parties;

  const anneeCourante = maintenant().getFullYear();
  // De la plus récente à la plus ancienne : sur une naissance comme sur une dépense, la
  // date cherchée est presque toujours proche d'aujourd'hui.
  const annees = Array.from(
    { length: fenetre.avant + fenetre.apres + 1 },
    (_, i) => anneeCourante + fenetre.apres - i,
  );
  const jours = Array.from({ length: joursDuMois(annee, mois) }, (_, i) => i + 1);

  const composer = (j: number, m: number, a: number): void => {
    // Un 31 choisi avant de passer à février est ramené au dernier jour réel, plutôt que
    // de produire une date que l'API refuserait.
    const jourValide = j && m && a ? Math.min(j, joursDuMois(a, m)) : j;
    const suivantes = { jour: jourValide, mois: m, annee: a };
    setParties(suivantes);
    onChange(composerTexte(suivantes));
  };

  return (
    <fieldset className="champ champ-date" aria-describedby={aide ? aideId : undefined}>
      <legend>{label}</legend>

      <div className="champ-date-listes">
        <span className="champ-date-partie">
          <label htmlFor={`${id}-jour`}>{traduction.jour}</label>
          <select
            id={`${id}-jour`}
            value={jour || ''}
            required={required}
            onChange={(evenement) => composer(Number(evenement.target.value), mois, annee)}
          >
            <option value="">—</option>
            {jours.map((valeur) => (
              <option key={valeur} value={valeur}>
                {valeur}
              </option>
            ))}
          </select>
        </span>

        <span className="champ-date-partie">
          <label htmlFor={`${id}-mois`}>{traduction.moisLabel}</label>
          <select
            id={`${id}-mois`}
            value={mois || ''}
            required={required}
            onChange={(evenement) => composer(jour, Number(evenement.target.value), annee)}
          >
            <option value="">—</option>
            {traduction.mois.map((nom, index) => (
              <option key={nom} value={index + 1}>
                {nom}
              </option>
            ))}
          </select>
        </span>

        <span className="champ-date-partie">
          <label htmlFor={`${id}-annee`}>{traduction.annee}</label>
          <select
            id={`${id}-annee`}
            value={annee || ''}
            required={required}
            onChange={(evenement) => composer(jour, mois, Number(evenement.target.value))}
          >
            <option value="">—</option>
            {annees.map((valeur) => (
              <option key={valeur} value={valeur}>
                {valeur}
              </option>
            ))}
          </select>
        </span>
      </div>

      {aide ? (
        <p className="aide" id={aideId}>
          {aide}
        </p>
      ) : null}
    </fieldset>
  );
}

/**
 * Date de naissance : la même saisie, sur une fenêtre qui ne regarde qu'en arrière.
 *
 * Proposer 2028 pour une naissance n'aurait pas de sens, et allongerait la liste de ce
 * qu'elle ne peut pas contenir.
 */
export function ChampDateNaissance(
  props: Omit<Parameters<typeof ChampDate>[0], 'fenetre'>,
) {
  return <ChampDate {...props} fenetre={{ avant: ANNEES_EN_ARRIERE, apres: 0 }} />;
}
